import AuthStore from 'auth-store';
import history from 'setup/history';
import jwt_decode from 'jwt-decode';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import { usePersistState } from 'helper';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { routes } from 'partial/routes';
import { useMutation, useQuery } from 'react-query';
import { isEmpty, fromPairs } from 'lodash';
import { toastSuccess } from 'partial/components/react-geek-toast';
import { getQR, mfaSetup } from 'modules/mfa/actions';
import * as actions from './actions';
import * as c from './constants';

export const useAuth = () => {
  const auth = useApiGet(c.AUTHENTICATION, {});
  useEffect(() => {
    const token = AuthStore.get('_token');
    const permissions = AuthStore.get('_permissions');
    req.set(c.AUTHENTICATION, {
      isAuthenticated: Boolean(token && permissions),
      userType: 'ADMINISTRATOR',
    });
  }, []);
  return [typeof auth.isAuthenticated === 'boolean', auth.isAuthenticated];
};

export const useLogin = () => {
  const isLoading = useApiLoading(c.LOGIN, 'post') ?? false;
  return [isLoading, actions.login];
};

export const useGetProfile = () => {
  const [profile, debounceSet] = usePersistState('profile', null);
  useEffect(() => {
    actions.getProfile(debounceSet);
  }, [debounceSet]);
  const get = useCallback(() => {
    actions.getProfile(debounceSet);
  }, [debounceSet]);
  return [profile, get];
};

export const useProfile = () => {
  const contx = useContext(c.ProfileContext);
  return [contx?.profile, contx?.getProfile];
};

export const useRoutePermission = () => {
  const permissions = JSON.parse(AuthStore.get('_permissions'));
  const newRoutes = useMemo(
    () =>
      routes
        .map((route) => {
          if (!route?.subRoutes) {
            if (isEmpty(route?.permissions)) return route;
            const access = route?.permissions?.filter(
              (routePerm) =>
                permissions?.[routePerm] &&
                permissions?.[routePerm]?.indexOf('r') !== -1,
            );
            if (isEmpty(access)) return null;
            return route;
          }
          if (route?.subRoutes) {
            const newSubRoutes = route?.subRoutes?.filter((subRoute) =>
              subRoute?.permissions?.every(
                (subRoutePerm) =>
                  permissions?.[subRoutePerm] &&
                  permissions?.[subRoutePerm]?.indexOf('r') !== -1,
              ),
            );
            if (isEmpty(newSubRoutes)) return null;
            return {
              ...route,
              subRoutes: newSubRoutes,
            };
          }
          return null;
        })
        .filter(Boolean),
    [permissions],
  );

  return newRoutes;
};

export const useVerifyMfaOtp = (onSuccess, onError) => {
  const { mutate, isLoading } = useMutation(actions.verifyMfaOtp, {
    onSuccess: (res) => {
      if (typeof onSuccess === 'function') onSuccess(res);
      const token = res?.data?.data?.token;
      const decoded = jwt_decode(token);
      const { scope } = decoded;
      const permissionsRaw = scope?.split(' ');
      const permissions = fromPairs(permissionsRaw?.map((x) => x.split(':')));
      AuthStore.set('_token', token);
      AuthStore.set('_permissions', JSON.stringify(permissions));
      req.set(c.AUTHENTICATION, {
        isAuthenticated: true,
        userType: 'ADMINISTRATOR',
      });
      if (typeof onSuccess === 'function') onSuccess(res);
      history.push('/dashboard');
    },
    onError: (err) => {
      if (typeof onError === 'function') onError(err?.response?.data?.message);
    },
  });
  return [isLoading, mutate];
};

export const useMfaSetup = (callBack = () => {}) => {
  const { mutate, isLoading } = useMutation(mfaSetup, {
    onSuccess: (res) => {
      const is_mfa_enabled = res?.data?.data?.is_mfa_enabled;
      toastSuccess(
        `Successfully ${is_mfa_enabled ? 'enabled' : 'disabled'} MFA`,
      );
      callBack();
    },
  });
  return [isLoading, mutate];
};

export const useQR = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['get-mfa-qr'],
    queryFn: getQR,
    retry: false,
  });
  const svgString = data?.data;
  const qrUrl = svgString
    ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
    : null;
  return [isLoading, qrUrl];
};
